import { Conversation, 
  Link, 
  Contact, 
  IConversationBulkAction, 
  MessageType, 
  ConversationBulkActionTypes,
  ConversationFilterType,
  MessageTypeString
} from '../models'
import { Module, ActionContext } from 'vuex'
import firebase from 'firebase/compat/app'
import { RootState, ConversationState } from './state_models'
import { sortedIndexBy } from 'lodash'
import { ConversationsService } from '../services'

let unsubscribeRealtime: () => void
let searchDebouncer: NodeJS.Timer
let firstSnapshot: boolean
let locationId: string | undefined
let seqFetching = 0
const limit = 20

const camelToSnakeCase = str => str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);

const isComingFromSearch = (filters: any) => {
  if (filters.status != ConversationFilterType.TYPE_ALL) {
    return true;
  }

  return filters.lastMessageType != null || filters.assignedTo != null || filters.query?.length > 2;
}

const checkIfConversationToBeRemoved = (state: any, data: any) => {
  //if filters have unread and data doesn't have unread_count > 0
  //if filters have read and data have unread_count > 0
  //if filters have starred and data's starred is false
  //if filters doesn't have starred and data's starred is true
  //if filters lastMessageType and data's lastMessageType is different than it
  //assignedTo filter
  if (data.last_message_type == 'TYPE_ACTIVITY') return true
  if (data.deleted) {
    return true;
  }

  if (state.filtersV2.status == "unread" && (typeof data.unread_count == "undefined" || data.unread_count == null || data.unread_count == 0) && !state.outboundSentConversationIds?.includes(data.id)) {
    return true;
  }

  if (state.filtersV2.status == "read" && typeof data.unread_count == "number" && data.unread_count > 0) {
    return true;
  }

  if (state.filtersV2.status == "starred" && (typeof data.starred == "undefined" || (typeof data.starred == "boolean" && !data.starred))) {
    return true;
  }

  if (state.filtersV2.status == "recents" && !data.inbox) {
    return true;
  }

  if (state.filtersV2.lastMessageType && state.filtersV2.lastMessageType != data.last_message_type) {
    return true;
  }

  if (state.filtersV2.assignedTo && state.filtersV2.assignedTo.length > 0) {

    if (state.filtersV2.assignedTo == "unassigned") {

      if (data.assigned_to && data.assigned_to.length > 0) return true

      return false
    } else {
      let assigneeList = state.filtersV2.assignedTo.split(',')

      if (data.assigned_to && data.assigned_to.length > 0) {
        if (assigneeList.includes(data.assigned_to)) return false
        return true
      }
      return true

    }
  }

  if (state.filtersV2.lastMessageDirection  && state.filtersV2.lastMessageDirection !== data.last_message_direction) {
    return true;
  }

  if (state.filtersV2.lastMessageAction  && state.filtersV2.lastMessageAction != data.last_outbound_message_action) {
    return true;
  }

  return false;
}

const convertObjectKeysCamelCaseToSnakeCase = (obj: any): any => {

  if (typeof obj == "undefined" || obj == null) {
    return obj;
  }
  if (typeof obj == "object") {
    if (Array.isArray(obj)) {
      let newObj: any[] = [];
      for (let item of obj) {
        newObj.push(convertObjectKeysCamelCaseToSnakeCase(item));
      }
      return newObj;
    } else {
      let newObj: any = {};
      Object.keys(obj).forEach((key) => {
        if (typeof obj[key] == "object") {
          newObj[camelToSnakeCase(key)] = convertObjectKeysCamelCaseToSnakeCase(obj[key]);
        } else {
          newObj[camelToSnakeCase(key)] = obj[key];
        }
      })
      return newObj;
    }
  } else {
    return obj;
  }
}

function getSortedIndex(
  conversations: Array<{ [key: string]: any }>,
  element: { [key: string]: any }, sortBy: string
): number {
  return sortedIndexBy(
    conversations,
    element,
    value => -value[sortBy]
  )
}

function addUpdateContact(
  documents,
  params: {
    currentTab: string
    payload: { [key: string]: any }
    dontAdd?: boolean
  },
  filtersV2: any
) {
  let scoring
  const index = documents.findIndex(obj => obj.id === params.payload.id)
  if (index !== -1) {
    scoring = documents[index].scoring?.map(s => Object.assign({}, s))
    documents.splice(index, 1)
  } else if (params.dontAdd) {
    return
  }
  
  const sortBy = filtersV2.sortBy ?? 'last_message_date'
  if (scoring) {
    params.payload.scoring = scoring
  }
  const sortedIndex = getSortedIndex(documents, params.payload, sortBy)
  if (documents.length < limit || sortedIndex !== documents.length) {
    documents.splice(sortedIndex, 0, Object.freeze(params.payload))
  } else {
  }
}

function checkForDuplicacy(documents: any[]) {
  let map = new Map();
  let newDocuments: any[] = [];
  for (let doc of documents) {
    if (!map[doc.id]) {
      newDocuments.push(doc)
      map[doc.id] = true
    }
  }

  return newDocuments
}

function removeContact(
  documents,
  params: { currentTab: string; payload: { [key: string]: any } }
) {
  const index = documents.findIndex(obj => obj.id === params.payload.id)
  if (index !== -1) {
    documents.splice(index, 1)
    return true
  }
  return false
}

function getConversationContactESData(contact: Contact) {
  if (!contact) return {}

  return {
    email: contact.email,
    phone: contact.phone,
    full_name: contact.fullName,
    company_name: contact.companyName,
    profile_photo: contact.profilePhoto,
    tags: contact.tags
  }
}

export const conversationV2: Module<ConversationState, RootState> = {
  namespaced: true,
  state: {
    all: {
      done: false,
      documents: [],
      last_document_date: '',
      total: 0
    },
    inbox: {
      done: false,
      documents: [],
      last_document_date: '',
    },
    unread: {
      done: false,
      documents: [],
      last_document_date: '',
    },
    search: {
      done: false,
      documents: [],
      last_document_date: '',
    },
    fetching: false,
    searching: false,
    currentTab: 'all',
    currentTabV2: '',
    searchTerm: '',
    triggerLinksMenuItem: {},
    filters: {
      user: ''
    },
    filtersV2: {
      assignedTo: '',
      lastMessageType: '',
      status: 'unread',
      query: '',
      locationId: '',
      startAfterDate: 0,
      sort: 'desc',
      scoreProfile: '',
      sortScoreProfile: '',
      scoreProfileMax: '',
      scoreProfileMin: ''
    },
    onboarding: false,
    popupStep: 1,
    resultsFetchedFromSearch: false,
    outboundSentConversationIds: [],
    newMessageData:null,
    userSavedFilters: null,
    gmbIntegration: null,
    collapseContent: false,
  },
  getters: {
    meta: (state: ConversationState) => {
      if (state.searchTerm) return state.search
      if (state.currentTab === 'all') return state.all
      if (state.currentTab === 'inbox') return state.inbox
      if (state.currentTab === 'unread') return state.unread
    },
    conversations: (state: ConversationState) => {
      if (state.searchTerm) return state.search.documents
      if (state.currentTab === 'all') return state.all.documents
      if (state.currentTab === 'inbox') return state.inbox.documents
      if (state.currentTab === 'unread') return state.unread.documents
    },
    metaV2: (state: ConversationState) => {
      return state.all;
    },
    resultsFetchedFromSearch: (state: ConversationState) => {
      return state.resultsFetchedFromSearch;
    },
    conversationsV2: (state: ConversationState) => {
      return state.all.documents
    },
    getTriggerLinksMenuItem: (state: ConversationState) => {
      return state.triggerLinksMenuItem
    },
    currentTabV2: (state: ConversationState) => {
      return state.currentTabV2
    },
    newMessageData:(state:ConversationState) => {
      return state.newMessageData
    },
    leftPanelFilters: (state:ConversationState) => {
      return state?.userSavedFilters?.conversations?.leftPanel
    },
    gmbIntegration: (state: ConversationState) => {
      return state.gmbIntegration
    }
  },
  mutations: {
    updateContactInfo: (state: ConversationState, params: {
      contact: Contact
    }) => {
      if (state.searchTerm) {
        const document = state.search.documents && state.search.documents.find(x => x.contact_id === params.contact.id)
        addUpdateContact(state.search.documents, {
          currentTab: 'search',
          payload: {
            ...document,
            ...getConversationContactESData(params.contact)
          },
          dontAdd: true
        },state.filtersV2) // Don't add documents that arent in the searched term array
      }

      let documents: Array<{ [key: string]: any }> = []
      let document
      if (state.currentTab === 'unread') {
        documents = state.unread.documents
        document = state.unread.documents && state.unread.documents.find(x => x.contact_id === params.contact.id)
      } else if (state.currentTab === 'inbox') {
        documents = state.inbox.documents
        document = state.inbox.documents && state.inbox.documents.find(x => x.contact_id === params.contact.id)
      } else if (state.currentTab === 'all') {
        documents = state.all.documents
        document = state.all.documents && state.all.documents.find(x => x.contact_id === params.contact.id)
      }

      if (document) {
        addUpdateContact(documents, {
          currentTab: state.currentTab,
          payload: {
            ...document,
            ...getConversationContactESData(params.contact)
          }
        },state.filtersV2)
      }
    },
    addOrUpdate: (
      state: ConversationState,
      params: {
        currentTab: string
        payload: { [key: string]: any }
        dontAdd?: boolean
      }
    ) => {
      if (state.searchTerm) {
        addUpdateContact(state.search.documents, { ...params, dontAdd: true }, state.filtersV2) // Don't add documents that arent in the searched term array
      }

      let documents: Array<{ [key: string]: any }> = []
      if (params.currentTab === 'unread') {
        documents = state.unread.documents
      } else if (params.currentTab === 'inbox') {
        documents = state.inbox.documents
      } else if (params.currentTab === 'all') {
        documents = state.all.documents
      }
      addUpdateContact(documents, params, state.filtersV2)
    },
    addOrUpdateV2: (
      state: ConversationState,
      params: {
        currentTab: string
        payload: { [key: string]: any }
        dontAdd?: boolean
      }
    ) => {
      let documents: Array<{ [key: string]: any }> = state.all.documents
      addUpdateContact(documents, {...params, dontAdd: state.searchTerm ? true : false}, state.filtersV2)
      documents = checkForDuplicacy(documents)

      if (typeof state.all.total == "number") {
        state.all.total = Math.max(state.all.total, documents.length)
      }
    },
    remove: (
      state: ConversationState,
      params: { currentTab: string; payload: { [key: string]: any } }
    ) => {
      if (state.searchTerm) {
        removeContact(state.search.documents, params)
      }
      let documents: Array<{ [key: string]: any }> = []
      if (params.currentTab === 'unread') {
        documents = state.unread.documents
      } else if (params.currentTab === 'inbox') {
        documents = state.inbox.documents
      } else if (params.currentTab === 'all') {
        documents = state.all.documents
      }
      removeContact(documents, params)
    },
    removeV2: (
      state: ConversationState,
      params: { currentTab: string; payload: { [key: string]: any } }
    ) => {
      let documents: Array<{ [key: string]: any }> = state.all.documents;
      let isContactRemoved = removeContact(documents, params)
      if (state.all.total && isContactRemoved) {
        state.all.total--;
      }
    },
    updateNewMessageData : (state: ConversationState, params: { newMessageData : { [key: string]: any }}) => {     
      if(params.newMessageData){
        state.newMessageData = convertObjectKeysCamelCaseToSnakeCase(JSON.parse(JSON.stringify(params.newMessageData)))
      }
      else{
          state.newMessageData = null
      } 
      
    },
    
    add: (
      state: ConversationState,
      params: {
        searchTerm: string
        currentTab: string
        results: Array<{ [key: string]: any }>
      }
    ) => {
      let documents: Array<{ [key: string]: any }>
      if (params.searchTerm) {
        documents = state.search.documents
      } else if (params.currentTab === 'unread') {
        documents = state.unread.documents
      } else if (params.currentTab === 'inbox') {
        documents = state.inbox.documents
      } else if (params.currentTab === 'all') {
        documents = state.all.documents
      }

      Object.entries(params.results).forEach(([key, value]) => {
        documents.push(
          Object.freeze({
            id: value._id,
            ...value._source,
          })
        )
      })
    },
    addV2: (
      state: ConversationState,
      params: {
        searchTerm: string
        currentTab: string
        results: Array<{ [key: string]: any }>,
        total: number,
        refresh: boolean
      }
    ) => {
      let documents: Array<{ [key: string]: any }>
      if (params.refresh) {
        state.all.documents = []
      }
      documents = state.all.documents
      state.all.total = params.total

      Object.entries(params.results).forEach(([key, value]) => {
        documents.push(
          Object.freeze({
            id: value._id,
            ...value,
          })
        )
      })
    },
    tab: (state: ConversationState, currentTab: string) => {
      state.currentTab = currentTab
    },
    term: (state: ConversationState, searchTerm: string) => {
      state.searchTerm = searchTerm
    },
    fetching: (state: ConversationState, fetching: boolean) => {
      state.fetching = fetching
    },
    searching: (state: ConversationState, searching: boolean) => {
      state.searching = searching
    },
    onboarding: (
      state: ConversationState,
      onboarding: boolean
    ) => {
      state.onboarding = onboarding;
    },
    collapseContent :
     (state: ConversationState,
      value: boolean
      ) => {
      state.collapseContent = value;
    },

    popupStep: (
      state: ConversationState,
      popupStep: number
    ) => {
      state.popupStep = popupStep;
    },
    finishedFetch: (state: ConversationState, payload: { searchTerm: string; currentTab: string, done: boolean; last_document_date: number; }) => {
      if (payload.searchTerm) {
        state.search.done = payload.done
        state.search.last_document_date = payload.last_document_date
      } else if (payload.currentTab === 'unread') {
        state.unread.done = payload.done
        state.unread.last_document_date = payload.last_document_date
      } else if (payload.currentTab === 'inbox') {
        state.inbox.done = payload.done
        state.inbox.last_document_date = payload.last_document_date
      } else if (payload.currentTab === 'all') {
        state.all.done = payload.done
        state.all.last_document_date = payload.last_document_date
      }
    },
    finishedFetchV2: (state: ConversationState, payload: { done: boolean; last_document_date: number; }) => {
      state.all.done = payload.done
      state.all.last_document_date = payload.last_document_date
    },
    clear: (state: ConversationState, filterClear: boolean) => {
      state.search = {
        done: false,
        last_document_date: '',
        documents: [],
      }
      state.unread = {
        done: false,
        last_document_date: '',
        documents: [],
      }
      state.inbox = {
        done: false,
        last_document_date: '',
        documents: [],
      }
      state.all = {
        done: false,
        last_document_date: '',
        documents: [],
      }
      state.fetching = false
      if (!filterClear) {
        state.currentTab = 'all'
        state.searchTerm = ''
        locationId = undefined
      }
    },
    clearV2: (state: ConversationState) => {
      state.all = {
        done: false,
        last_document_date: '',
        documents: [],
        total: 0
      }
      state.fetching = false
    },
    clearAllV2: (state: ConversationState, locationId: string) => {
      if (unsubscribeRealtime) {
        unsubscribeRealtime()
      }
      state.all = {
        done: false,
        last_document_date: '',
        documents: [],
        total: 0
      }
      state.resultsFetchedFromSearch = false;
      state.filtersV2 = {
        assignedTo: '',
        lastMessageType: '',
        status: 'unread',
        query: '',
        locationId,
        startAfterDate: 0,
        sort: 'desc',
        scoreProfile: '',
        sortScoreProfile: '',
        scoreProfileMax: '',
        scoreProfileMin: ''
      }
      state.outboundSentConversationIds = []
    },
    clearSearchTerm: (state: ConversationState) => {
      state.searchTerm = '';
    },
    clearCurrentTabV2: (state: ConversationState) => {
      state.currentTabV2 = '';
    },
    clearSearch: (state: ConversationState) => {
      state.fetching = false
      state.search = {
        done: false,
        last_document_date: '',
        documents: [],
      }
    },
    setTriggerLinks: (
      state: ConversationState,
      links: { value: any; text: string; location_id: string }
    ) => {
      state.triggerLinksMenuItem = links
    },
    setFiltersV2: (state: ConversationState, filters: {
      status: string;
      assignedTo: string;
      lastMessageType: string;
      sort: string;
     }) => {
      state.filtersV2 = filters
    },
    setCurrentTabV2: (state: ConversationState, value: string) => {
      state.currentTabV2 = value
    },
    filterReadsFromUnreadList(state: ConversationState) {
      state.unread.documents = state.unread.documents.filter(
        doc => doc.unread_count > 0
      )
    },
    resultsFetchedFromSearch(state: ConversationState, value: boolean) {
      state.resultsFetchedFromSearch = value;
    },
    updateOutboundSentConversationIds(state: ConversationState, value: string) {
      //if not inside unread filter
      if (value == null) {
        state.outboundSentConversationIds = []
        return
      }
      if (!state.outboundSentConversationIds.includes(value)) {
        state.outboundSentConversationIds.push(value)
      }
    },
    updateUserSavedFilters(state: ConversationState, value: any) {
      state.userSavedFilters = {...value}
    }
  },
  actions: {
    closeNewMessageConversation: async(context: ActionContext<ConversationState, RootState>, params:{ conversationId: string }) => {
      if(context.state.newMessageData){
        if(context.state.newMessageData.is_in_list){
          let documents: Array<{ [key: string]: any }> = context.state.all.documents
          addUpdateContact(documents, {currentTab:'all', payload: context.state.newMessageData, dontAdd: context.state.searchTerm ? true : false}, context.state.filtersV2)

        }
        context.commit('updateNewMessageData' , { newMessageData: null })
      }
    },
    updateConversationsOnNewMessage: async(context: ActionContext<ConversationState, RootState>, params: { newMessageData : { [key: string]: any }}) => {
      let currentTab = context.state.currentTab
      if(params.newMessageData && params.newMessageData.contactId && currentTab === 'all'){
        
        if(params.newMessageData.id === 'new-contact-conversation'){
          const newConversation = params.newMessageData
          context.commit('updateNewMessageData', {newMessageData : { ...newConversation, isInList:false}})

        }else {
          let element = context.state.all.documents.filter((conversation) => conversation.id === params.newMessageData.id)[0]

          if(element) {
            
            context.commit('removeV2', { currentTab: 'all', payload: element })
            context.commit('updateNewMessageData', { newMessageData : {...element , isInList:true}})

          }else{
            element = await Conversation.getById(params.newMessageData.id)

            let contactDetails = await Contact.getById(params.newMessageData.contactId)
            
            let lastMessageType = MessageTypeString[MessageType[element._data.last_message_type]]

            element = { 
              id:element._id , 
              ...element._data, 
              fullName: contactDetails.fullName, 
              contactName: contactDetails.name, 
              email: contactDetails.email, 
              phone: contactDetails.phone,
              lastMessageType
            }
            
            context.commit('updateNewMessageData', { newMessageData : {...element, isInList:false} })
          }
        }
      }
    },
    submitFiltersV2: async (
      context: ActionContext<ConversationState, RootState>,
      params: { filters: any, refresh: boolean, locationId?: string, tab: string , newMessageData?: any}
    ) => {

      if (params.filters.sort != context.state.filtersV2.sort) {
        params.refresh = true;
      }

      if (params.refresh) {
        context.commit('clearV2');
      }
      context.commit('setFiltersV2', params.filters);
      context.commit("setCurrentTabV2", params.tab)

      if (params.locationId) {
        locationId = params.locationId
      }
      await context.dispatch('loadMoreV2', {
        refresh: params.refresh, newMessageData: params.newMessageData
      });
    },
    submitFilters: async (
      context: ActionContext<ConversationState, RootState>,
      filters
    ) => {
      context.commit('clear', true)
      context.commit('setFilters', filters)
      await Promise.all([
        context.dispatch('loadMore', { currentTab: 'all' }),
        context.dispatch('loadMore', { currentTab: 'inbox' }),
        context.dispatch('loadMore', { currentTab: 'unread' }),
      ])
    },
    markAsReadRemoveFromUnreads: async (
      context: ActionContext<ConversationState, RootState>,
      params: { conversationId: string }
    ) => {
      context.commit('remove', {
        currentTab: 'unread',
        payload: { id: params.conversationId },
      })
    },
    fetchTriggerLinks: async (
      context: ActionContext<ConversationState, RootState>,
      params: { locationId: string }
    ) => {
      let _locationId = locationId
      if  (!_locationId) {
        _locationId = params?.locationId
      }
      if (
        !context.state.triggerLinksMenuItem.location_id ||
        context.state.triggerLinksMenuItem.location_id !== _locationId
      ) {
        const snapshot = await Link.fetchAllByLocation(_locationId).get()
        const links = snapshot.docs
          .map((d: any) => new Link(d))
          .map(l => {
            return {
              text: l.name,
              value: `{{trigger_link.${l.id}}}`,
            }
          })
        context.commit('setTriggerLinks', {
          menu: links,
          text: 'Trigger links',
          location_id: locationId,
        })
        return { menu: links, text: 'Trigger links', location_id: _locationId }
      } else {
        return {}
      }
    },
    syncAllV2: async (
      context: ActionContext<ConversationState, RootState>,
      params: { locationId: string; noListener?: boolean; stickyFilterEnabled?: boolean }
    ) => {
      context.commit('clearAllV2', params.locationId)
      if (locationId != params.locationId || !params.stickyFilterEnabled) {
        context.commit('clearSearchTerm')
        context.commit('clearCurrentTabV2')
      }
      locationId = params.locationId
      if (params.noListener) return
      await context.dispatch('startListeningV2')
    },
    setCurrentTab: async (
      context: ActionContext<ConversationState, RootState>,
      currentTab: string
    ) => {
      context.commit('tab', currentTab)
      if (context.state.searchTerm) {
        context.commit('clearSearch')
        context.commit('searching', true)
        await context.dispatch('loadMore')
      }
    },
    setSearchTerm: async (
      context: ActionContext<ConversationState, RootState>,
      searchTerm: string
    ) => {
      context.commit('term', searchTerm)
      context.commit('clearSearch')
      if (!searchTerm) {
        context.commit('searching', false)
        context.commit('fetching', false)
        return
      }

      context.commit('searching', true)
      if (searchDebouncer) clearTimeout(searchDebouncer)
      searchDebouncer = setTimeout(() => {
        context.dispatch('loadMore')
      }, 300)
    },
    setSearchTermV2: async (
      context: ActionContext<ConversationState, RootState>,
      searchTerm: string
    ) => {
      context.commit('term', searchTerm)
      if (searchTerm.length > 0 && searchTerm.length < 3) {
        context.commit('searching', false)
        context.commit('fetching', false)
        return;
      }

      context.commit('clearV2');
      context.commit('searching', true)
      if (searchDebouncer) clearTimeout(searchDebouncer)
      searchDebouncer = setTimeout(() => {
        context.dispatch('loadMoreV2', { refresh: true })
      }, 300)
    },
    setOnboarding: async (
      context: ActionContext<ConversationState, RootState>,
      onboarding: boolean
    ) => {
      context.commit('onboarding', onboarding)
    },
    setCollapseContent: async (
      context: ActionContext<ConversationState, RootState>,
      value: boolean
    ) => {
      context.commit('collapseContent', value)
    },

    setPopupStep: async (
      context: ActionContext<ConversationState, RootState>,
      popupStep: number
    ) => {
      context.commit('popupStep', popupStep)
    },
    
    loadMoreV2: async (
      context: ActionContext<ConversationState, RootState>,
      params: { refresh: boolean , newMessageData?: any}) => {
      let currentTab;
      currentTab = context.state.currentTab
      const data = context.state.all

      //maintaining pagination
      if (!params.refresh && data.last_document_date) {
        context.state.filtersV2.startAfterDate = data.last_document_date
      } else {
        context.state.filtersV2.startAfterDate = null;
      }

      context.commit('fetching', true)
      const localSeqFetching = ++seqFetching
      context.state.filtersV2.locationId = locationId
      const searchTerm = context.state.searchTerm

      if (searchTerm && searchTerm.length >= 3) {
        context.state.filtersV2.query = searchTerm
      } else {
        context.state.filtersV2.query = null
      }

      if (params.refresh && isComingFromSearch(context.state.filtersV2)) {
        context.commit("resultsFetchedFromSearch", true);
      } else {
        context.commit("resultsFetchedFromSearch", false);
      }

      const results = await Conversation.getConversationsV2(context.state.filtersV2);

      

      if (searchTerm && localSeqFetching !== seqFetching) return

      context.commit('searching', false)
      context.commit('fetching', false)
      results.conversations = convertObjectKeysCamelCaseToSnakeCase(JSON.parse(JSON.stringify(results.conversations)));
      context.commit('addV2', {
        currentTab,
        searchTerm,
        results: results.conversations,
        total: results.total,
        refresh: params.refresh
      })

      let lastDocumentDate = (results.conversations[
        results.conversations.length - 1
      ] as any)?.last_message_date

      if (context.state.filtersV2.sortBy === 'last_manual_message_date') {
        lastDocumentDate = (results.conversations[
          results.conversations.length - 1
        ] as any)?.sort
      }      

      context.commit('finishedFetchV2', {
        done: results.conversations.length !== limit,
        last_document_date:
          results.conversations.length > 0
            ? lastDocumentDate
            : '',
      })
      
      if(params.newMessageData && params.newMessageData.contactId && currentTab === 'all'){
        context.dispatch('updateConversationsOnNewMessage', { newMessageData : params.newMessageData })
      }
      

    },
    startListeningV2: (context: ActionContext<ConversationState, RootState>) => {
      firstSnapshot = true
      if (unsubscribeRealtime) unsubscribeRealtime()
      unsubscribeRealtime = Conversation.collectionRef()
        .where('location_id', '==', locationId)
        .orderBy('date_updated', 'desc')
        .limit(10)
        .onSnapshot(
          async (snapshot: firebase.firestore.QuerySnapshot) => {
            if (firstSnapshot) {
              firstSnapshot = false
              return
            }

            for (let i = snapshot.docChanges().length - 1; i >= 0; i--) {
              const docChange = snapshot.docChanges()[i]
              // console.log("docChange.doc.id", docChange.doc.id)
              let data: { [key: string]: any } = {
                id: docChange.doc.id,
                ...docChange.doc.data(),
              }

              try
              {
                if (data.date_added) data.date_added = data.date_added.toMillis()
              if (data.date_updated)
                data.date_updated = data.date_updated.toMillis()
              if (data.last_message_date)
                data.last_message_date = data.last_message_date.toMillis()

                if (data.last_manual_message_date)
                data.last_manual_message_date = data.last_manual_message_date.toMillis()
              }
              
              
              catch(e)
              {}

              if (docChange.type === 'removed') continue

              if (typeof data.last_message_type == "number") {
                data.last_message_type = MessageType[data.last_message_type];
              }
              if (checkIfConversationToBeRemoved(context.state, data)) {
                context.commit('removeV2', { currentTab: 'all', payload: data })
                continue;
              }

              //dedup contact fetch by storing contact data in vuex
              if (data.contact_id) {
                const contact = await context.dispatch('contacts/syncGet', data.contact_id, { root: true })
                data = Object.assign(getConversationContactESData(contact), data)
              }
              
              if(context.state.newMessageData && (data.contact_id ===  context.state.newMessageData.contact_id )){
                context.commit('updateNewMessageData' , { newMessageData: null })
                // context.dispatch('closeNewMessageConversation', {conversationId: data.id})
                continue;
              }

              context.commit('addOrUpdateV2', {
                currentTab: 'all',
                payload: data,
              })
            }
          }, err => {
            console.error(err)
            throw err
          }
        )
    },
    bulkAction: async (context: ActionContext<ConversationState, RootState>,
      data: IConversationBulkAction) => {
        const results = await Conversation.performBulkAction(data);
        let type;
        if (data.value) {
          type = data.action == ConversationBulkActionTypes.MarkAsRead ? "unread" : (data.action == ConversationBulkActionTypes.Starred ? "starred" : "delete")
        } else {
          type = data.action == ConversationBulkActionTypes.MarkAsRead ? "read" : (ConversationBulkActionTypes.Archive ? 'archive' : "unstarred" )
        }
        results.type = type;

        if (results.success) {
          let message = ''
          if (results.type == 'read') {
            message = `Success: ${results.count} Conversation${
              results.count > 1 ? 's' : ''
            } marked as read`
          } else if (results.type == 'unread') {
            message = `Success: ${results.count} Conversation${
              results.count > 1 ? 's' : ''
            } marked as unread`
          } else if (results.type == 'starred') {
            message = `Success: ${results.count} Conversation${
              results.count > 1 ? 's' : ''
            } marked as starred`
          } else if (results.type == 'unstarred') {
            message = `Success: ${results.count} Conversation${
              results.count > 1 ? 's' : ''
            } marked as unstarred`
          } else if (results.type == 'archive') {
            message = `Success: ${results.count} Conversation${
              results.count > 1 ? 's' : ''
            } archived`
          } else {
            message = `Success: ${results.count} Conversation${
              results.count > 1 ? 's' : ''
            } deleted`
          }

          return { success: true, message, type };
        } else {
          return { success: false, message: "Unable to perform bulk actions", type };
        }
    },
    fakeUpdateAfterBulkActions:(context: ActionContext<ConversationState, RootState>, params: {conversationIds: string[], type: string}) => {
      let bulkActionConversations: any[] = JSON.parse(JSON.stringify(context.state.all.documents.filter((doc: any) => {
        return params.conversationIds.findIndex(id => id == doc.id) > -1
      }))).map((conversation) => {
        switch(params.type)
        {
          case 'read':
            conversation.unread_count = 0;
            break;
          case 'unread':
            conversation.unread_count = 1;
            break;
          case 'delete':
            conversation.deleted = true;
            break;
          case 'starred':
            conversation.starred = true;
            break;
          case 'unstarred':
            conversation.starred = false;
            break;
        }

        return conversation
      })

      for (let conversation of bulkActionConversations) {
        if (checkIfConversationToBeRemoved(context.state, conversation)) {
          context.commit('removeV2', { currentTab: 'all', payload: conversation })
          continue
        }

        context.commit('addOrUpdateV2', {
          currentTab: 'all',
          payload: conversation,
        })
      }

      if (context.state.all.documents.length == 0) {
        context.commit('fetching', true)
        setTimeout(async () => {
          await context.dispatch('loadMoreV2', { refresh: true })
        }, 3000);
      }
    },
    refreshUnread: (context: ActionContext<ConversationState, RootState>) => {
      context.commit('filterReadsFromUnreadList')
    },
    updateSentMessage: (context: ActionContext<ConversationState, RootState>, conversationId: string) => {
      if (context.state.filtersV2.status == "unread") {
        context.commit('updateOutboundSentConversationIds', conversationId)
      } else {
        context.commit('updateOutboundSentConversationIds', null)
      }
    },
    logout: {
      root: true,
      handler: async (context: ActionContext<ConversationState, RootState>) => {
        if (unsubscribeRealtime) {
          unsubscribeRealtime()
        }
      },
    },
    fetchUserSavedFilters: async (context: ActionContext<ConversationState, RootState>, params: {userId: string, locationId: string}) => {
      let filterData = await ConversationsService.fetchFilters(params.userId, params.locationId)
      context.commit('updateUserSavedFilters', filterData)
    },
    updateUserSavedFiltersLocally: (context: ActionContext<ConversationState, RootState>, data: any) => {
      context.commit('updateUserSavedFilters', data)
    },
    saveGmbIntegration: (context: ActionContext<ConversationState, RootState>, data: any) => {
      context.state.gmbIntegration = data
    }
  },
}
